import { format, parse } from 'date-fns';
import * as R from 'ramda';
import { UnpackNestedValue } from 'react-hook-form';

import fetchWithCatch from '@/lib/fetch-with-catch';
import type {
  ParticipantCountry,
  ParticipantGender,
} from '@/lib/participant-field-options';
import { Lottery2Api } from '@/types/apis/lottery2';
import { LotteryV2Pot } from '@/types/views/generic';

const LOTTERY2_PARTICIPATE_TYPE = 'web';

export interface ParticipantPersonalData {
  gender: ParticipantGender;
  email: string;
  firstName: string;
  lastName: string;
  country: ParticipantCountry;
  phone: string;
  birthdate: string; // dd.MM.yyyy
  address: string;
  zipCode: string;
  city: string;
}

export interface AdditionalData {
  terms: boolean;
  emailMarketing: boolean;
  captcha: string;
}

export type LotteryFormData = ParticipantPersonalData &
  AdditionalData &
  Record<string, string>;

/**
 * Transfers from dd.MM.yyyy to yyyy-MM-dd
 * @param {string} date - E.g.: 01.01.1990
 * @return {string} - E.g.: 01.01.1990
 */
const mapToLotteryV2ApiDateFormat = (date: string) =>
  format(parse(date, 'dd.MM.yyyy', new Date()), 'yyyy-MM-dd');

export const participate = async (
  payload: UnpackNestedValue<
    ParticipantPersonalData & AdditionalData & Record<string, string>
  >,
  lotteryPot: LotteryV2Pot,
) => {
  const body: Lottery2Api.ParticipatePayload = {
    form_data: {
      accept_terms: payload.terms,
      allow_email_marketing: payload.emailMarketing,
      captcha: payload.captcha,

      gender: payload.gender,
      first_name: payload.firstName,
      last_name: payload.lastName,
      email: payload.email,
      birthday: mapToLotteryV2ApiDateFormat(payload.birthdate),
      phone: payload.phone,
      address: payload.address,
      city: payload.city,
      country: payload.country,
      zip: payload.zipCode,

      ...R.fromPairs(
        lotteryPot.customFields.map((field) => [
          `cf_${field.id}`,
          payload[`cf_${field.id}`],
        ]),
      ),
    },
    lottery_id: lotteryPot.lotteryId,
    member_id: null,
    pot_id: lotteryPot.potId,
    type: LOTTERY2_PARTICIPATE_TYPE,
  };

  return fetchWithCatch<Lottery2Api.ParticipateResponseData>(() =>
    fetch(`/api/proxy/lottery2`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }),
  );
};
